import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import theme from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { DropdownsProvider } from './contexts/DropdownsContext';
import RequireAuth from './components/RequireAuth';
import RequireRole from './components/auth/RequireRole';
import ProtectedRoute from './components/ProtectedRoute';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';

// Eagerly loaded — needed immediately on first page load
import Login from './pages/auth/Login';
import Handoff from './pages/auth/Handoff';
import Register from './pages/auth/Register';
import Dashboard from './pages/Dashboard';
import ServiceOrderList from './components/service_orders/ServiceOrderList';
import ServiceOrderDetail from './components/service_orders/ServiceOrderDetail';
import PublicReportViewer from './pages/PublicReportViewer';
import PublicCertificateViewer from './pages/PublicCertificateViewer';
import PublicQuotationApiRedirect from './components/PublicQuotationApiRedirect';
import { trainingRoutes } from './routes/trainingRoutes';
import { salesRoutes } from './routes/salesRoutes';
import { hrRoutes } from './routes/hrRoutes';

// Lazy-loaded pages — downloaded only when the user navigates to them
const DashboardPage       = lazy(() => import('./pages/dashboard/DashboardPage'));
const ServiceOrderCreate  = lazy(() => import('./pages/ServiceOrderCreate'));
const Inventory           = lazy(() => import('./pages/Inventory'));
const Users               = lazy(() => import('./pages/Users'));
const Settings            = lazy(() => import('./pages/Settings'));
const Reporting           = lazy(() => import('./pages/Reporting'));
const FlashReport         = lazy(() => import('./pages/reporting/FlashReport'));
const DetailedReport      = lazy(() => import('./pages/reporting/DetailedReport'));
const MasterData          = lazy(() => import('./pages/settings/master_data/MasterData'));
const UnitsOfMeasure      = lazy(() => import('./pages/settings/master_data/inventory/UnitsOfMeasure'));
const Suppliers           = lazy(() => import('./pages/settings/master_data/inventory/Suppliers'));
const Locations           = lazy(() => import('./pages/settings/master_data/inventory/Locations'));
const Companies           = lazy(() => import('./pages/settings/master_data/companies/Companies'));
const CompanyForm         = lazy(() => import('./pages/settings/master_data/companies/CompanyForm'));
const ServiceCategories   = lazy(() => import('./pages/settings/master_data/service_catalog/ServiceCategories'));
const ServiceTypes        = lazy(() => import('./pages/settings/master_data/service_catalog/ServiceTypes'));
const ServiceRateCards    = lazy(() => import('./pages/settings/master_data/service_catalog/ServiceRateCards'));
const EquipmentPage       = lazy(() => import('./pages/inventory/EquipmentPage'));
const ConsumablePage      = lazy(() => import('./pages/inventory/ConsumablePage'));
const UserCreate          = lazy(() => import('./pages/users/UserCreate'));
const UserDetail          = lazy(() => import('./pages/users/UserDetail'));
const UserEdit            = lazy(() => import('./pages/users/UserEdit'));
const RoleCreate          = lazy(() => import('./pages/settings/RoleCreate'));
const Profile             = lazy(() => import('./pages/Profile'));
const Notifications       = lazy(() => import('./pages/Notifications'));
const AdminDashboard      = lazy(() => import('./pages/admin/AdminDashboard'));
const DocumentManagement       = lazy(() => import('./pages/DocumentManagement'));
const CustomerReportingList    = lazy(() => import('./pages/customer/CustomerReportingList'));

// Invoicing Routes
const InvoiceDetailPage   = lazy(() => import('./pages/invoicing/InvoiceDetailPage'));
const InvoiceFormPage     = lazy(() => import('./pages/invoicing/InvoiceFormPage'));
const ARDashboardPage     = lazy(() => import('./pages/invoicing/ARDashboardPage'));
const GenerateInvoicePage = lazy(() => import('./pages/invoicing/GenerateInvoicePage'));

const App = () => {
    const suspenseFallback = (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
            <CircularProgress />
        </Box>
    );

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <SnackbarProvider>
                <AuthProvider>
                    <DropdownsProvider>
                        <NotificationProvider>
                            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                        <Suspense fallback={suspenseFallback}>
                        <Routes>
                            {/* Auth Routes */}
                            <Route element={<AuthLayout />}>
                                <Route path="/login" element={<Login />} />
                                <Route path="/handoff" element={<Handoff />} />
                                <Route path="/register" element={<Register />} />
                            </Route>

                            {/* Public Routes - No authentication required */}
                            <Route path="/public/report/:reportType/:reportId/:token" element={<PublicReportViewer />} />
                            <Route path="/public/certificate/:certId/:token" element={<PublicCertificateViewer />} />
                            <Route path="/public/quotation/:token/*" element={<PublicQuotationApiRedirect />} />
                            <Route path="/public/quotation/:token" element={<PublicQuotationApiRedirect />} />

                            {/* Report-only view for the app's in-app browser: no navigation or layout */}
                            <Route path="/embed/reports/flash/:id" element={
                                <RequireAuth>
                                    <ProtectedRoute module="reporting" action="view">
                                        <FlashReport />
                                    </ProtectedRoute>
                                </RequireAuth>
                            } />

                            {/* Protected Routes */}
                            <Route element={<RequireAuth><MainLayout /></RequireAuth>}>
                                <Route path="/" element={<Dashboard />} />
                                <Route path="/dashboard" element={<Dashboard />} />
                                <Route path="/dashboard-legacy" element={<DashboardPage />} />

                                {/* Profile Route */}
                                <Route path="/profile" element={<Profile />} />

                                {/* Notifications Route */}
                                <Route path="/notifications" element={
                                    <ProtectedRoute module="notifications" action="view">
                                        <Notifications />
                                    </ProtectedRoute>
                                } />

                                {/* Service Orders Routes */}
                                <Route path="/service-orders" element={<ServiceOrderList />} />
                                <Route path="/service-orders/new" element={<ServiceOrderCreate />} />
                                <Route path="/service-orders/:id" element={<ServiceOrderDetail />} />
                                <Route path="/service-orders/:id/edit" element={<ServiceOrderCreate />} />

                                {/* Invoicing Routes */}
                                <Route path="/invoicing/:id" element={
                                    <ProtectedRoute module="invoicing" action="view">
                                        <InvoiceDetailPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/invoicing/:id/edit" element={
                                    <ProtectedRoute module="invoicing" action="view">
                                        <InvoiceFormPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/ar-dashboard/generate-invoice" element={
                                    <ProtectedRoute module="ar_dashboard" action="view">
                                        <GenerateInvoicePage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/ar-dashboard" element={
                                    <ProtectedRoute module="ar_dashboard" action="view">
                                        <ARDashboardPage />
                                    </ProtectedRoute>
                                } />

                                {/* Inventory Routes */}
                                <Route path="/inventory" element={
                                    <ProtectedRoute module="inventory" action="view">
                                        <Inventory />
                                    </ProtectedRoute>
                                } />
                                <Route path="/inventory/equipment/:id" element={
                                    <ProtectedRoute module="inventory" action="view">
                                        <EquipmentPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/inventory/equipment/:id/edit" element={
                                    <ProtectedRoute module="inventory" action="view">
                                        <EquipmentPage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/inventory/consumables/:id" element={
                                    <ProtectedRoute module="inventory" action="view">
                                        <ConsumablePage />
                                    </ProtectedRoute>
                                } />
                                <Route path="/inventory/consumables/:id/edit" element={
                                    <ProtectedRoute module="inventory" action="view">
                                        <ConsumablePage />
                                    </ProtectedRoute>
                                } />

                                {/* Users Routes */}
                                <Route path="/users" element={
                                    <ProtectedRoute module="users" action="view">
                                        <Users />
                                    </ProtectedRoute>
                                } />
                                <Route path="/users/create" element={
                                    <ProtectedRoute module="users" action="view">
                                        <UserCreate />
                                    </ProtectedRoute>
                                } />
                                <Route path="/users/:id" element={
                                    <ProtectedRoute module="users" action="view">
                                        <UserDetail />
                                    </ProtectedRoute>
                                } />
                                <Route path="/users/:id/edit" element={
                                    <ProtectedRoute module="users" action="view">
                                        <UserEdit />
                                    </ProtectedRoute>
                                } />

                                {/* Document Management */}
                                <Route path="/documents" element={
                                    <ProtectedRoute module="documents" action="view">
                                        <DocumentManagement />
                                    </ProtectedRoute>
                                } />

                                {/* Reporting Routes */}
                                <Route path="/reporting" element={
                                    <ProtectedRoute module="reporting" action="view">
                                        <Reporting />
                                    </ProtectedRoute>
                                } />
                                <Route path="/customer/reports" element={
                                    <ProtectedRoute module="reporting" action="view">
                                        <CustomerReportingList />
                                    </ProtectedRoute>
                                } />
                                <Route path="/flash-report/:serviceId" element={
                                    <ProtectedRoute module="reporting" action="view">
                                        <FlashReport />
                                    </ProtectedRoute>
                                } />
                                <Route path="/detailed-report/:serviceId/:type" element={
                                    <ProtectedRoute module="reporting" action="view">
                                        <DetailedReport />
                                    </ProtectedRoute>
                                } />
                                <Route path="/reports/flash/:id/view" element={
                                    <ProtectedRoute module="reporting" action="view">
                                        <FlashReport />
                                    </ProtectedRoute>
                                } />
                                <Route path="/reports/flash/:id/edit" element={
                                    <ProtectedRoute module="reporting" action="view">
                                        <FlashReport />
                                    </ProtectedRoute>
                                } />
                                <Route path="/reports/flash/:id" element={
                                    <ProtectedRoute module="reporting" action="view">
                                        <FlashReport />
                                    </ProtectedRoute>
                                } />
                                <Route path="/reports/details/:id" element={
                                    <ProtectedRoute module="reporting" action="view">
                                        <DetailedReport />
                                    </ProtectedRoute>
                                } />
                                <Route path="/reports/details/:id/edit" element={
                                    <ProtectedRoute module="reporting" action="view">
                                        <DetailedReport />
                                    </ProtectedRoute>
                                } />

                                {/* Reflection Dashboard Routes */}
                                <Route path="/reflection-dashboard" element={
                                    <ProtectedRoute module="admin_dashboard" action="view">
                                        <AdminDashboard />
                                    </ProtectedRoute>
                                } />

                                {/* Master Data Routes */}
                                <Route path="/master-data" element={
                                    <ProtectedRoute module="master_data" action="view">
                                        <MasterData />
                                    </ProtectedRoute>
                                }>
                                    <Route index element={<MasterData />} />
                                <Route path="inventory/units-of-measure" element={<UnitsOfMeasure />} />
                                <Route path="inventory/suppliers" element={<Suppliers />} />
                                <Route path="inventory/locations" element={<Locations />} />
                                <Route path="inventory/equipment" element={<Inventory />} />
                                <Route path="inventory/equipment/:id" element={<EquipmentPage />} />
                                <Route path="inventory/equipment/:id/edit" element={<EquipmentPage />} />
                                <Route path="inventory/consumables" element={<Inventory />} />
                                <Route path="inventory/consumables/:id" element={<ConsumablePage />} />
                                <Route path="inventory/consumables/:id/edit" element={<ConsumablePage />} />
                                <Route path="companies" element={<Companies />} />
                                <Route path="companies/create" element={<CompanyForm />} />
                                <Route path="companies/:id/edit" element={<CompanyForm />} />
                                <Route path="service-catalog/categories" element={<ServiceCategories />} />
                                <Route path="service-catalog/service-types" element={<ServiceTypes />} />
                                <Route path="service-catalog/rate-cards" element={<ServiceRateCards />} />
                                </Route>



                                {/* Sales: enquiries, quotations, purchase orders */}
                                {salesRoutes}

                                {/* Training Routes */}
                                {trainingRoutes}

                                {/* Attendance / Leave / Expenses Routes */}
                                {hrRoutes}

                                {/* Settings Routes */}
                                <Route path="/settings" element={
                                    <Settings />
                                } />
                                <Route path="/settings/roles/create" element={
                                    <RequireRole permissions={['add_roles']}>
                                        <RoleCreate />
                                    </RequireRole>
                                } />

                                {/* Catch all route */}
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Route>
                        </Routes>
                        </Suspense>
                    </Router>
                    </NotificationProvider>
                    </DropdownsProvider>
                </AuthProvider>
            </SnackbarProvider>
            <ToastContainer position="top-right" autoClose={3000} />
        </ThemeProvider>
    );
};

export default App; 